import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { fontSize } from '../..';
import { addParameterToChart } from '../../redux/Parameters/ParametersAction';
import Slider from './DisplayTypes/Slider';
import Checkbox from './DisplayTypes/Checkbox';
import Radio from './DisplayTypes/Radio';
import Range from './DisplayTypes/Range';
import CalendarRange from './DisplayTypes/CalendarRange';
import Calendar from './DisplayTypes/Calendar';
import Switch from './DisplayTypes/Switch';
import InputField from './DisplayTypes/InputField';
import { IParameterSessionProps } from '../../redux/Parameters/ParameterInterface';

export interface ParameterProps {
  parameter: IParameterSessionProps;
  handleEditParameter: (parameterId: string) => void;
  editMode: boolean;
  propKey: string;
  showDash?: boolean;
  backgroundColor?: string;
  color?: string;
}

const ParameterContainer = ({
  parameterMenu,
  setParameterMenu,
  isTile,
  backgroundColor,
  color,
}: {
  parameterMenu?: boolean;
  setParameterMenu?: (value: boolean) => void;
  isTile?: boolean;
  backgroundColor?: string;
  color?: string;
}) => {
  const { parameters: parameter, tabTileProps } = useSelector((state: any) => ({
    parameters: state.parameters,
    tabTileProps: state.tabTileProps,
  }));
  const dispatch = useDispatch();
  const [parameters, setParameters] = useState<any>([]);
  const [manualParametersMenu, setManualParametersMenu] = useState(true);
  const isEdit = tabTileProps.dashMode === 'Edit';
  const isPresent = tabTileProps.dashMode === 'Present';
  const isOpen = isPresent ? parameterMenu : manualParametersMenu;

  const propKey = useMemo(
    () => `${tabTileProps?.selectedTabId}.${tabTileProps?.selectedTileId}`,
    [tabTileProps?.selectedTabId, tabTileProps?.selectedTileId],
  );

  const shouldDefaultOpen = useMemo(() => {
    return parameter.savedParameters?.some((p: any) => p.showParameter);
  }, [parameter.savedParameters]);

  useEffect(() => {
    if (isPresent && setParameterMenu) {
      setParameterMenu(shouldDefaultOpen);
    }
  }, [isPresent, setParameterMenu, shouldDefaultOpen]);
  const handleMenuToggle = () => {
    if (isEdit && setManualParametersMenu) {
      setManualParametersMenu(!manualParametersMenu); // Manual toggle in Edit
    } else if (isPresent && setParameterMenu) {
      setParameterMenu(!parameterMenu); // Global toggle in Present
    }
  };

  const handleEditParameter = (parameterId: string) => {
    dispatch(addParameterToChart(propKey, parameterId));
  };

  const renderParameterComponent = (parameter: any, index: number) => {
    switch (parameter.columns?.displayType) {
      case 'slider (single selection)':
        return (
          <Slider
            parameter={parameter}
            handleEditParameter={handleEditParameter}
            editMode={tabTileProps.dashMode === 'Edit'}
            showDash={tabTileProps.showDash}
            propKey={propKey}
            color={color}
          />
        );
      case 'range (range selection)':
        return (
          <Range
            parameter={parameter}
            handleEditParameter={handleEditParameter}
            editMode={tabTileProps.dashMode === 'Edit'}
            showDash={tabTileProps.showDash}
            propKey={propKey}
            color={color}
          />
        );
      case 'calendar (range selection)':
        return (
          <CalendarRange
            parameter={parameter}
            handleEditParameter={handleEditParameter}
            editMode={tabTileProps.dashMode === 'Edit'}
            showDash={tabTileProps.showDash}
            propKey={propKey}
            color={color}
            backgroundColor={backgroundColor}
          />
        );
      case 'calendar (single selection)':
        return (
          <Calendar
            parameter={parameter}
            handleEditParameter={handleEditParameter}
            editMode={tabTileProps.dashMode === 'Edit'}
            showDash={tabTileProps.showDash}
            propKey={propKey}
            color={color}
            backgroundColor={backgroundColor}
          />
        );
      case 'switch':
        return (
          <Switch
            parameter={parameter}
            handleEditParameter={handleEditParameter}
            editMode={tabTileProps.dashMode === 'Edit'}
            showDash={tabTileProps.showDash}
            propKey={propKey}
            color={color}
          />
        );
      default:
        if (parameter.columns?.displayType?.includes('input')) {
          return (
            <InputField
              parameter={parameter}
              handleEditParameter={handleEditParameter}
              editMode={tabTileProps.dashMode === 'Edit'}
              showDash={tabTileProps.showDash}
              propKey={propKey}
              color={color}
              backgroundColor={backgroundColor}
            />
          );
        } else if (parameter.columns?.displayType?.includes('checkbox')) {
          return (
            <Checkbox
              parameter={parameter}
              handleEditParameter={handleEditParameter}
              editMode={tabTileProps.dashMode === 'Edit'}
              showDash={tabTileProps.showDash}
              propKey={propKey}
              color={color}
            />
          );
        } else if (parameter.columns?.displayType?.includes('radio')) {
          return (
            <Radio
              parameter={parameter}
              handleEditParameter={handleEditParameter}
              editMode={tabTileProps.dashMode === 'Edit'}
              showDash={tabTileProps.showDash}
              propKey={propKey}
              color={color}
              backgroundColor={backgroundColor}
            />
          );
        }
        return null;
    }
  };

  useEffect(() => {
    setParameters(parameter.savedParameters.filter((parameter: any) => parameter.showParameter));
  }, [parameter.savedParameters]);

  return (
    <Box
      sx={{
        pt: 0.5,
        pb: isOpen ? 1.5 : 0,
        bgcolor: backgroundColor,
        height: 'fit-content',
        borderBottom: '3px solid lightgray',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 0.55,
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: fontSize.large,
            fontWeight: 600,
            color: color,
            px: isTile ? 0.5 : 0,
            textAlign: 'start',
            marginLeft: isTile ? '6px' : '0px',
          }}
        >
          Parameters
        </Typography>
        <Tooltip
          title={`${isOpen ? 'Collapse' : 'Expand'} Parameters`}
          slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
        >
          <IconButton
            sx={{
              mr: isTile ? '1.02rem' : '0rem',
              height: '16px',
              width: '16px',
              color: color,
              cursor: 'pointer',
            }}
            onClick={handleMenuToggle}
          >
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Tooltip>
      </Box>

      {isOpen && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 0.5,
            gap: 2,
            marginLeft: isTile ? '-2px' : '-10px',
            marginRight: isTile ? '0px' : '-17px',
          }}
        >
          {parameters.map((parameter: any, index: number) => (
            <Box
              key={parameter.parameterId}
              sx={{
                borderColor: color || 'rgba(84, 81, 81, 0.5)',
                transition: 'all 0.2s ease-in-out',
                paddingBottom: '0.2rem',
                '&:last-child': {
                  borderBottom: 'none',
                  paddingBottom: 0,
                },
              }}
            >
              {renderParameterComponent(parameter, index)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ParameterContainer;
